import { ReactNode, useEffect } from "react";

import { useToast } from "@hightouch/htui";
import { generatePath, useNavigate, useParams } from "src/router";

import { PageContentSkeleton } from "src/components/loading";
import {
  AIDRole,
  useAIDPermissions,
} from "src/components/permission/use-aid-permissions";
import { useUser } from "src/contexts/user-context";

type Props = {
  minRequiredRole: AIDRole;
  children: ReactNode;
  redirect: string;
};

// PermissionedRoute for AI Decisioning pages: AID access is a dual read of
// the workspace decision engine grants and the legacy LaunchDarkly role, which
// a single v2 permission check cannot express. Impersonating Hightouch staff
// always pass, as they do in the AID resolvers.
export function AidPermissionedRoute({
  children,
  minRequiredRole,
  redirect,
}: Props) {
  const params = useParams();
  const { isPermitted: isPermittedByRole, isLoading } = useAIDPermissions({
    minRequiredRole,
  });
  const navigate = useNavigate();
  const { user } = useUser();
  const { toast } = useToast();

  const isPermitted = isPermittedByRole || Boolean(user?.is_impersonating);

  useEffect(() => {
    if (isLoading || isPermitted) return;
    toast({
      id: "unauthorized-navigation",
      title: "You do not have permission to view this page",
      variant: "warning",
    });
    navigate(generatePath(redirect, params));
  }, [isLoading, isPermitted, navigate, params, redirect, toast]);

  if (isLoading || !isPermitted) {
    return <PageContentSkeleton />;
  }

  return <>{children}</>;
}
